import React, { useState } from "react";
import { Box, Typography, TextField, Paper, Divider, Grid } from "@mui/material";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";

const KaTeXScreen: React.FC = () => {
    const [expression, setExpression] = useState<string>(
        "% \\f is defined as #1f(#2) using the\n% macro\n\\f\\relax{x} = \\int_{-\\infty}^\\infty\n\\f\\hat\\xi\\,e^{2 \\pi i \\xi x}\n\\,d\\xi"
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "primary.main" }}>
                KaTeX Live Preview
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
                Type LaTeX expressions below to see them rendered in real-time.
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={3} sx={{ p: 2, bgcolor: "#2d2d2d", height: "400px" }}>
                        <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
                            Type an expression:
                        </Typography>
                        <TextField
                            multiline
                            fullWidth
                            variant="standard"
                            value={expression}
                            onChange={(e) => setExpression(e.target.value)}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    color: "#f8f8f2",
                                    fontFamily: "monospace",
                                    fontSize: "1.1rem",
                                    height: "320px",
                                    alignItems: "flex-start",
                                    overflowY: "auto"
                                },
                            }}
                            placeholder="Enter LaTeX here..."
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={3} sx={{ p: 2, height: "400px", overflowY: "auto", border: "1px solid", borderColor: "divider" }}>
                        <Typography variant="h6" sx={{ color: "text.primary", mb: 2 }}>
                            See how it renders with KaTeX:
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "200px",
                            fontSize: "1.5rem"
                        }}>
                            <Markdown
                                remarkPlugins={[remarkMath]}
                                rehypePlugins={[rehypeKatex]}
                            >
                                {`$$${expression}$$`}
                            </Markdown>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Paper elevation={1} sx={{ p: 3, bgcolor: "info.light", borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Sample Questions
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { label: "Quadratic Formula", math: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}" },
                            { label: "Euler's Identity", math: "e^{i\\pi} + 1 = 0" },
                            { label: "Standard Deviation", math: "\\sigma = \\sqrt{\\frac{1}{N} \\sum_{i=1}^N (x_i - \\mu)^2}" },
                            { label: "Schrödinger Equation", math: "i\\hbar \\frac{\\partial}{\\partial t} \\Psi(x,t) = [ -\\frac{\\hbar^2}{2m} \\frac{\\partial^2}{\\partial x^2} + V(x,t) ] \\Psi(x,t)" }
                        ].map((sample, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Box
                                    onClick={() => setExpression(sample.math)}
                                    sx={{
                                        p: 2,
                                        border: "1px solid",
                                        borderColor: "primary.light",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        "&:hover": { bgcolor: "primary.light", borderColor: "primary.main" }
                                    }}
                                >
                                    <Typography variant="subtitle2">{sample.label}</Typography>
                                    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.8rem", color: "text.secondary" }}>
                                        {sample.math}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Box>
        </Box>
    );
};

export default KaTeXScreen;
